import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { initials } from "../../lib/assignees";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { Plus, Trash2, Users, Pencil } from "lucide-react";

const COLORS = ["#AF52DE", "#007AFF", "#30B0C7", "#34C759", "#FF9500", "#FF2D55", "#8E8E93"];
const inputCls = "mt-1.5 w-full h-10 px-3.5 rounded-xl bg-slate-100/80 dark:bg-white/10 border border-slate-200/60 dark:border-white/10 text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]/50 focus:bg-white dark:focus:bg-white/15 transition-all";
const labelCls = "text-xs font-medium text-slate-500 uppercase tracking-wider";
const EMPTY = { name: "", color: COLORS[0], customer_id: "", member_ids: [] };

export const AdminTeams = ({ customers, teams, onChanged }) => {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, [teams]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (t) => { setForm({ name: t.name, color: t.color, customer_id: t.customer_id || "", member_ids: t.member_ids || [] }); setEditing(t.id); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, customer_id: form.customer_id || null };
      if (editing === "new") await api.post("/admin/teams", payload);
      else await api.patch(`/admin/teams/${editing}`, payload);
      toast.success(editing === "new" ? "Grup oluşturuldu" : "Grup güncellendi");
      setEditing(null);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`"${t.name}" grubu silinsin mi? Task atamalarından kaldırılır.`)) return;
    await api.delete(`/admin/teams/${t.id}`);
    toast.success("Grup silindi");
    onChanged();
  };

  const toggleMember = (id) => setForm((f) => ({ ...f, member_ids: f.member_ids.includes(id) ? f.member_ids.filter((x) => x !== id) : [...f.member_ids, id] }));
  const eligible = users.filter((u) => u.role === "admin" || !form.customer_id || u.customer_id === form.customer_id);
  const customerName = (id) => customers.find((c) => c.id === id)?.name;

  return (
    <div className="animate-fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">Gruplar</h1>
          <p className="text-sm text-slate-500 mt-1.5">Kişileri gruplayın, task'ları tek seferde ekiplere atayın</p>
        </div>
        <button data-testid="admin-add-team-button" onClick={openNew}
          className="h-10 px-4 rounded-xl bg-[#0066CC] hover:bg-[#0052A3] active:scale-[0.98] text-white text-sm font-semibold transition-all shadow-lg shadow-[#0066CC]/25 flex items-center gap-2">
          <Plus size={16} /> Yeni Grup
        </button>
      </div>

      <div data-testid="admin-team-list" className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {teams.map((t) => {
          const members = users.filter((u) => (t.member_ids || []).includes(u.id));
          return (
            <div key={t.id} data-testid="admin-team-card" className="group bg-white/90 dark:bg-[#1C1C1E]/90 backdrop-blur-md rounded-2xl border border-slate-200/70 dark:border-white/10 p-5 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-xl text-white flex items-center justify-center shrink-0" style={{ background: t.color }}><Users size={18} /></span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display font-semibold text-slate-900 dark:text-white tracking-tight truncate">{t.name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{t.customer_id ? customerName(t.customer_id) || "Müşteri" : "Genel (tüm projeler)"} · {members.length} kişi</p>
                </div>
                <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity">
                  <button data-testid="admin-team-edit-button" onClick={() => openEdit(t)} className="w-8 h-8 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 text-slate-400 hover:text-slate-700 dark:hover:text-white flex items-center justify-center"><Pencil size={14} /></button>
                  <button data-testid="admin-team-delete-button" onClick={() => remove(t)} className="w-8 h-8 rounded-lg hover:bg-[#FF2D55]/10 text-slate-400 hover:text-[#FF2D55] flex items-center justify-center"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="flex items-center -space-x-2 mt-4">
                {members.slice(0, 6).map((u) => (
                  <span key={u.id} title={u.name} className={`w-7 h-7 rounded-full border-2 border-white dark:border-[#1C1C1E] text-white text-[10px] font-bold flex items-center justify-center ${u.role === "admin" ? "bg-slate-900 dark:bg-slate-600" : "bg-[#007AFF]"}`}>{initials(u.name)}</span>
                ))}
                {members.length === 0 && <span className="text-xs text-slate-400">Üye yok</span>}
              </div>
            </div>
          );
        })}
        {teams.length === 0 && <div className="col-span-full text-center py-16 text-slate-400 text-sm">Henüz grup yok. Örn: "İpekyol Ekibi" veya "Plena Tasarım".</div>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing === "new" ? "Yeni Grup" : "Grubu Düzenle"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <label className={labelCls}>Grup Adı</label>
              <input data-testid="team-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="İpekyol Ekibi" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Bağlı Müşteri</label>
              <select data-testid="team-customer-select" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value, member_ids: [] })} className={inputCls}>
                <option value="">Genel — tüm projelerde atanabilir</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Renk</label>
              <div className="flex items-center gap-2 mt-2">
                {COLORS.map((c) => (
                  <button type="button" key={c} data-testid={`team-color-${c.replace("#", "")}`} onClick={() => setForm({ ...form, color: c })}
                    className={`w-7 h-7 rounded-full transition-transform hover:scale-110 ${form.color === c ? "ring-2 ring-offset-2 ring-slate-400 dark:ring-offset-[#1C1C1E]" : ""}`} style={{ background: c }} />
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Üyeler <span className="normal-case tracking-normal text-slate-400">({form.member_ids.length})</span></label>
              <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-slate-200/70 dark:border-white/10 divide-y divide-slate-100 dark:divide-white/5">
                {eligible.map((u) => (
                  <label key={u.id} data-testid="team-member-option" className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5">
                    <Checkbox checked={form.member_ids.includes(u.id)} onCheckedChange={() => toggleMember(u.id)} className="rounded-md data-[state=checked]:bg-[#007AFF] data-[state=checked]:border-[#007AFF]" />
                    <span className={`w-6 h-6 rounded-full text-white text-[9px] font-bold flex items-center justify-center ${u.role === "admin" ? "bg-slate-900 dark:bg-slate-600" : "bg-[#007AFF]"}`}>{initials(u.name)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-slate-800 dark:text-slate-100 truncate">{u.name}</span>
                      <span className="block text-[10px] text-slate-400 truncate">{u.role === "admin" ? "Plena" : u.customer_name}</span>
                    </span>
                  </label>
                ))}
                {eligible.length === 0 && <p className="px-3 py-4 text-xs text-slate-400 text-center">Uygun kullanıcı yok</p>}
              </div>
            </div>
            <button data-testid="team-save-button" type="submit" disabled={saving}
              className="w-full h-10 rounded-xl bg-[#0066CC] hover:bg-[#0052A3] text-white text-sm font-semibold transition-all disabled:opacity-60">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTeams;
